/**
 * `aria-disabled`, e não `disabled`.
 *
 * O motivo de o controle estar indisponível está escrito no `title` desde
 * a onda 46 — e nunca chegou a ninguém. O navegador suprime evento de
 * ponteiro em controle `disabled`, então o `title` não aparece no
 * passar do mouse; e `disabled` sai da ordem de tabulação, então o
 * teclado não alcança o controle para ouvir a explicação.
 *
 * A explicação existia, era boa, e não rodava. É a mesma família do
 * `.trilho-os` (onda 163) e do que o doc 55 chama de regra escrita e não
 * ligada: o pior estado, porque tem aparência de resolvido.
 *
 * Com `aria-disabled` o controle continua focável e sob o mouse — o
 * `title` aparece, o leitor de tela anuncia «indisponível» e lê o motivo.
 * O clique é barrado no manipulador, que é onde a recusa tem que morar.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { api, mensagemDoErro } from '../api';
import { useSessao } from '../sessao';
import { NomeDaAmostra, nomeDaAmostra, type AmostraComNome } from '../NomeDaAmostra';
import { faixaProfundidade, hojeIso, rotulo } from '../formato';
import { Aviso, Campo, Carregando, Dialogo, ErroDaApi, SeloDoEnsaio, Vazio, simboloDoEnsaio } from '../ui';
import { FormAmostraPrevista, FormExcluirPrevista, FormProgramar } from './OsForms';
import type { Os } from './OsForms';
import { AcoesDaOs, OsPeloNumero, UUID } from './OsFicha';
import { FaixaDoEscopo, SaldoDoMetodo, linhaDoServidor } from './FaixaDoEscopo';
import type { LinhaDoEscopo } from './FaixaDoEscopo';
import { AbasDaOs, CabecalhoDaOs, proximoPasso, ReguaDaOs } from './OsMoldura';
import {
  contarUsoDoModelo, escoposDeEnsaio, gradeDaOs, gravarInstrucao,
  modelosDeInstrucao, reprogramar, salvarGrade, salvarModeloDeInstrucao,
} from '../api/programacao';
import type {
  AmostraDaGrade, BateriaDaGrade, Escopo, GradeDaOs, MetodoDaGrade, ModeloDeInstrucao,
} from '../api/programacao';
import { EtiquetasDepoisDaProgramacao } from './EtiquetasDaAmostra';

/**
 * Programar ensaios — o trilho, a sanfona e a grade.
 *
 * ─── O que esta tela responde ──────────────────────────────────────────
 *
 * «O usuário pensa: Quais ensaios as amostras da OS XXXXXXX deverão ser
 *  feitos? Então a programação fica mais fácil olhando por OS. E deve ser
 *  de maneira rápida na mesma página.» — Humberto, 30/08/2026.
 *
 * ─── As três coisas que a onda 46 acrescentou ──────────────────────────
 *
 * 1. **O TRILHO DE OS na lateral.** «Eu ainda gosto do modo sanfona
 *    deles, com a lista de OSs na lateral esquerda e abrindo as amostras
 *    no lado direito.» Antes era preciso voltar à listagem para trocar de
 *    OS; agora é um clique, e o trilho já mostra escopo e prazo de todas.
 *
 * 2. **DOIS MODOS sobre o mesmo estado.** A sanfona é para LER — uma
 *    amostra por bloco, com status e instrução à vista. A grade é para
 *    MARCAR trinta amostras. As duas perguntas existem no mesmo dia, e
 *    ter as duas com um botão entre elas não é indecisão: é reconhecer
 *    isso. Trocar de modo nunca perde marcação pendente, porque o estado
 *    é um só e o modo é só a forma de desenhá-lo.
 *
 * 3. **A INSTRUÇÃO DE ENSAIO.** «É importante que seja possível adicionar
 *    instruções de ensaio para alguns ensaios, principalmente
 *    adensamento, triaxial, cisalhamento» — e, na volta seguinte, «a
 *    instrução deve ser por amostra e por ensaio, pois para amostras
 *    diferentes pode haver variação». Como o ensaio JÁ É o par (amostra,
 *    método), ela mora nele, e a gaveta abre pela célula ou pela coluna.
 *
 * ─── O que a tela não decide ───────────────────────────────────────────
 *
 * Repetir ensaio que já existe. Célula com ensaio vivo mostra o STATUS e
 * não vira caixa: repetir é gesto próprio, com justificativa, e o ensaio
 * novo nasce apontando para o anterior. Repetição silenciosa é como o
 * laboratório acaba com dois resultados do mesmo material e nenhum
 * critério para escolher entre eles.
 */

/** Amostra que já saiu do acervo não recebe ensaio novo. */
const FORA = ['descartada', 'consumida', 'devolvida', 'rejeitada', 'subdividida'];

/**
 * Ensaio que já saiu da programação e virou trabalho de bancada.
 *
 * A célula dele não se desmarca: cancelar um ensaio com dado lançado
 * jogaria fora trabalho que alguém fez, e cancelar um aprovado apagaria
 * resultado técnico (restrição 25 do briefing).
 */
const INTOCAVEIS = ['aprovado', 'aguardando_verificacao', 'em_processamento',
  'dados_completos', 'devolvido_para_correcao'];

/* 267: a sigla da célula vem do selo único (`simboloDoEnsaio`, ui.tsx). */

const chave = (a: string, m: string) => `${a}|${m}`;

/**
 * O GRUPO de amostras que a grade mostra — e sobre o qual a coluna e a
 * bateria agem (0188).
 *
 * «15 amostras deformadas com estes seis ensaios; 15 indeformadas com os
 * mesmos mais cisalhamento e adensamento» é o pedido comum, e até aqui
 * cada bateria marcava as trinta. Escolher o grupo primeiro e marcar a
 * coluna depois é o que transforma 120 cliques em seis. O grupo só
 * FILTRA o que está à vista — nunca desmarca o que já foi marcado.
 */
type Grupo = 'todas' | 'recebidas' | 'por_chegar' | 'sem_ensaio'
  | 'deformada' | 'indeformada' | 'cp_moldado';
/*
 * 257 (§9.3 da ideia do fluxo da OS): os RECORTES são as abas da grade —
 * Todas · Recebidas · Por chegar · Sem ensaio, cada um com a contagem —, e
 * o tipo da amostra fica num segundo grupo, só quando a OS tem mais de um.
 * Continua sendo UM filtro: escolher um recorte desfaz o tipo, e vice-versa.
 */
const RECORTES: { chave: Grupo; nome: string }[] = [
  { chave: 'todas', nome: 'Todas' },
  { chave: 'recebidas', nome: 'Recebidas' },
  { chave: 'por_chegar', nome: 'Por chegar' },
  { chave: 'sem_ensaio', nome: 'Sem ensaio' },
];
const TIPOS: { chave: Grupo; nome: string }[] = [
  { chave: 'deformada', nome: 'Deformadas' },
  { chave: 'indeformada', nome: 'Indeformadas' },
  /* 0209: o CP moldado pelo cliente — o chip só aparece quando há algum */
  { chave: 'cp_moldado', nome: 'CPs moldados' },
];
const GRUPOS = [...RECORTES, ...TIPOS];
/**
 * «Sem ensaio» olha o que está GRAVADO (ensaio vivo ou marca na prevista),
 * e não o que se marcou agora: a linha não pode sumir do recorte no meio
 * do gesto que a está programando.
 */
const noGrupo = (a: AmostraDaGrade, g: Grupo, comEnsaio: Set<string>) => (
  g === 'todas' ? true
    : g === 'recebidas' ? !a.prevista
      : g === 'por_chegar' ? !!a.prevista
        : g === 'sem_ensaio' ? !comEnsaio.has(a.id)
          : a.tipo_amostra === g
);

/*
 * 267: a BUSCA e o «agrupar por furo» da grade (§9.3 da ideia do fluxo).
 *
 * A busca casa o código, a identificação de campo, o furo, a camada e o
 * tipo, sem acento e sem caixa: «sp 03», «SP-03» e «sp03» acham a mesma
 * amostra. Ela estreita o recorte — e, como o recorte, é sobre ela que a
 * coluna e a bateria agem: marcar a coluna com a busca «SP-03» marca só
 * as amostras do SP-03.
 */
const semAcento = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/[\s._-]+/g, '');
export const casaBusca = (a: AmostraDaGrade, busca: string) => {
  const q = semAcento(busca);
  if (!q) return true;
  return [a.codigo, a.identificacao_recebida, a.ponto_investigacao, a.camada,
    a.tipo_amostra === 'cp_moldado' ? 'CP moldado' : a.tipo_amostra,
    a.prevista ? 'por chegar' : null]
    .some((v) => v && semAcento(v).includes(q));
};
/** O furo da linha, para agrupar; sem furo vai para o fim. */
export const furoDe = (a: AmostraDaGrade) => a.ponto_investigacao?.trim() || '';
const ordemDoFuro = (x: string, y: string) =>
  x === y ? 0 : !x ? 1 : !y ? -1
    : x.localeCompare(y, 'pt-BR', { numeric: true, sensitivity: 'base' });

export function Programar() {
  const { id = '' } = useParams();
  if (!id) return <Vazio>Abra a programação pela ficha da ordem de serviço.</Vazio>;
  /* O número da OS no endereço (favorito, link antigo) vira o id, na aba. */
  if (!UUID.test(id)) return <OsPeloNumero numero={id} aba="programacao" />;
  return <TelaProgramacao osId={id} key={id} />;
}

/**
 * `/os/:id/programar` → `/os/:id/programacao` (257).
 *
 * O endereço antigo está em favoritos, e-mails e nos links que outras telas
 * ainda mandam. Ele continua chegando — à aba, sem deixar a etapa no
 * histórico do navegador e sem perder a busca (`?…`).
 */
export function ParaAProgramacao() {
  const { id = '' } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/os/${id}/programacao${search}${hash}`} replace />;
}

/* ── A aba «Programação» da OS (257) ───────────────────────────────────
 *
 * Era uma tela à parte, com a lista de OS na lateral (o «trilho», onda 46)
 * e um cabeçalho próprio. A ideia do fluxo da OS (§9.3) a põe DENTRO da OS:
 * o mesmo cabeçalho, a mesma régua e as mesmas abas das outras etapas, na
 * largura toda. Trocar de OS é pela lista de OS ou pela busca — o trilho
 * gastava 264 px repetindo a trilha (U6).
 */
function TelaProgramacao({ osId }: { osId: string }) {
  const { pode } = useSessao();
  const [os, setOs] = useState<Os | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  const carregarOs = useCallback(async () => {
    try { setOs(await api<Os>(`/os/${osId}`)); } catch (e) { setErro(e); }
  }, [osId]);
  useEffect(() => { void carregarOs(); }, [carregarOs]);

  if (erro && !os) return <ErroDaApi erro={erro} />;
  if (!os) return <Carregando texto="Abrindo a programação" />;

  /* O próximo passo do cabeçalho, menos o que leva a esta mesma aba. */
  const passo = proximoPasso(os, pode);
  const fora = passo && passo.para.startsWith(`/os/${os.id}/programacao`) ? null : passo;

  return (
    <div className="ficha-os programacao-os">
      <CabecalhoDaOs os={os} passo={fora} acoes={<AcoesDaOs os={os} />} />
      <ReguaDaOs os={os} aba="programacao" semMedicao={!pode('medicao.ver')}
        semAmostras={!pode('amostra.ver')} semProgramacao={!pode('ensaio.ver')} />
      <AbasDaOs os={os} aba="programacao" semQuantitativos={!pode('medicao.ver')}
        semAmostras={!pode('amostra.ver')} semProgramacao={!pode('ensaio.ver')} />
      <GradeDaOsTela osId={osId} aoSalvar={() => void carregarOs()} />
    </div>
  );
}

/* ── A grade da OS ────────────────────────────────────────────────── */

type Modo = 'sanfona' | 'grade';

function GradeDaOsTela({ osId, aoSalvar }: { osId: string; aoSalvar?: () => void }) {
  const [dados, setDados] = useState<GradeDaOs | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<unknown>(null);
  const [feito, setFeito] = useState<string | null>(null);
  /* 287: cada confirmação conta uma vez — o atalho das etiquetas olha de novo. */
  const [vezEtq, setVezEtq] = useState(0);
  const [reprogramando, setReprogramando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  /** As colunas visíveis, por id de método. Ordem = ordem do catálogo. */
  const [colunas, setColunas] = useState<Set<string>>(new Set());
  /** As células marcadas: `amostra|metodo`. */
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [dataPrevista, setDataPrevista] = useState(hojeIso());
  const [abrindoBusca, setAbrindoBusca] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [novaAmostra, setNovaAmostra] = useState<'serie' | 'uma' | null>(null);
  /* 227: a amostra por chegar que nunca vai chegar */
  const [excluir, setExcluir] = useState<{ id: string; nome: string | null; ensaios: number } | null>(null);

  /* 257: a grade é o modo padrão — as colunas SÃO o escopo da OS, e é
     nela que se marca. A sanfona continua a um clique, para ler. */
  const [modo, setModo] = useState<Modo>('grade');
  /* 267: a busca e o agrupamento por furo. */
  const [busca, setBusca] = useState('');
  const [porFuro, setPorFuro] = useState(false);
  /** As amostras escolhidas para a ação em lote (257). */
  const [selecionadas, setSelecionadas] = useState<Set<string>>(new Set());
  /** A amostra aberta na ficha à direita (257) — a sanfona virou ficha. */
  const [ficha, setFicha] = useState<string | null>(null);
  /** O que a ação em lote não marcou por falta de saldo. */
  const [semSaldo, setSemSaldo] = useState<string | null>(null);
  /* 258: o recorte pode vir no endereço — «Programar as Y» do recebimento
     chega com `?recorte=sem_ensaio`, e a grade abre nas que esperam. */
  const [params] = useSearchParams();
  const [grupo, setGrupo] = useState<Grupo>(() => {
    const r = params.get('recorte');
    return RECORTES.some((x) => x.chave === r) ? r as Grupo : 'todas';
  });
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const [escopos, setEscopos] = useState<Escopo[]>([]);
  /** Escopos LIGADOS. Vazio = ainda não carregou; então tudo aparece. */
  const [ligados, setLigados] = useState<Set<string>>(new Set());
  const [gaveta, setGaveta] = useState<AlvoDaGaveta | null>(null);
  const [massa, setMassa] = useState(false);
  const [instruirVarias, setInstruirVarias] = useState(false);
  /**
   * Instrução escrita para células que AINDA NÃO EXISTEM como ensaio.
   *
   * Só se grava instrução em ensaio gravado — a coluna mora em `ensaio`.
   * A promessa da programação em massa («escreva a instrução uma vez») se
   * cumpre assim: a receita fica aqui até o «Salvar», e logo depois é
   * aplicada aos ensaios que acabaram de nascer.
   */
  const [instrucaoPendente, setInstrucaoPendente] =
    useState<Map<string, string>>(new Map());

  useEffect(() => {
    void escoposDeEnsaio().then((e) => {
      setEscopos(e);
      setLigados(new Set(e.map((x) => x.codigo)));
    }).catch(() => {});
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const bruto = await gradeDaOs(osId);
      /*
       * Uma grade só (0161): as amostras que ainda não chegaram entram
       * como linhas, com `id` prefixado — a célula marcada nelas grava a
       * previsão, e a previsão vira ensaio sozinha quando a amostra
       * chegar. Quem olha a grade vê a OS inteira, chegada ou não.
       */
      const previstasComoLinhas = (bruto.previstas ?? []).map((p) => ({
        id: `prev:${p.id}`,
        codigo: 'por chegar',
        status: 'prevista',
        estado_custodia: 'prevista',
        identificacao_recebida: p.identificacao_prevista,
        ponto_investigacao: p.origem_prevista,
        profundidade_inicial: p.profundidade_inicial ?? null,
        profundidade_final: p.profundidade_final ?? null,
        camada: p.observacoes, tipo_amostra: p.tipo_amostra ?? null, prevista: true,
        /* 0190: «📍 projeto · 690.123 E / 6.987.654 N (22)» ao lado do nome */
        coordenada_prevista: p.utm_e != null && p.utm_n != null
          ? `📍 ${p.origem_coordenada ?? 'projeto'} · `
            + `${Math.round(Number(p.utm_e)).toLocaleString('pt-BR')} E / `
            + `${Math.round(Number(p.utm_n)).toLocaleString('pt-BR')} N`
            + (p.utm_fuso ? ` (${p.utm_fuso})` : '')
          : null,
      }));
      const g = {
        ...bruto,
        amostras: [...bruto.amostras, ...previstasComoLinhas],
        previsto: [
          ...bruto.previsto,
          ...(bruto.previstas ?? []).flatMap((p) => p.metodo_ids
            .map((m) => ({ amostra_id: `prev:${p.id}`, metodo_id: m }))),
        ],
      };
      setDados(g);

      /*
       * As colunas iniciais: o que a OS previu MAIS o que já foi
       * programado. Programado entra mesmo já estando feito porque
       * esconder o que existe é como alguém programa de novo.
       */
      const iniciais = new Set<string>();
      /* 257: o escopo da OS abre como coluna, programado ou não — a OS
         2610363 abria com 22 CPs, MR.Q15 contratado e zero colunas (§3.2). */
      for (const m of g.metodos) if (m.na_os) iniciais.add(m.id);
      for (const p of g.previsto) iniciais.add(p.metodo_id);
      for (const p of g.programado) iniciais.add(p.metodo_id);
      setColunas(iniciais);

      const fora = new Set(g.amostras.filter((a) => FORA.includes(a.status))
        .map((a) => a.id));
      const vivos = g.programado
        .filter((e) => !['cancelado', 'substituido'].includes(e.status))
        .map((e) => chave(e.amostra_id, e.metodo_id));

      const inicial = new Set(vivos);
      for (const p of g.previsto) {
        const k = chave(p.amostra_id, p.metodo_id);
        /* Linha que ainda não chegou: o marcado É a previsão, sempre.
           Amostra recebida: a previsão só pré-marca na primeira vez. */
        if (p.amostra_id.startsWith('prev:')) inicial.add(k);
        else if (g.programacao.nunca_programada && !fora.has(p.amostra_id)) inicial.add(k);
      }
      setMarcadas(inicial);

      /* A primeira amostra abre sozinha: sanfona toda fechada é uma tela
         que não mostra nada e obriga um clique antes de qualquer leitura. */
      setAbertas(new Set(g.amostras.slice(0, 1).map((a) => a.id)));
      setSelecionadas(new Set());
      setSemSaldo(null);
    } catch (e) { setErro(e); }
    finally { setCarregando(false); }
  }, [osId]);

  useEffect(() => { void carregar(); }, [carregar]);

  /** O ensaio vivo de cada célula, com a instrução que ele já carrega. */
  const existente = useMemo(() => {
    const m = new Map<string, {
      id: string; status: string; instrucao: string | null;
    }>();
    for (const e of dados?.programado ?? []) {
      if (['cancelado', 'substituido'].includes(e.status)) continue;
      m.set(chave(e.amostra_id, e.metodo_id),
        { id: e.id, status: e.status, instrucao: e.instrucao });
    }
    return m;
  }, [dados]);

  /**
   * 0182: as células de amostra «por chegar» que JÁ ESTÃO GRAVADAS na
   * previsão, com a instrução que cada uma carrega. É o equivalente do
   * `existente` para o que ainda não é ensaio — e é o que faz o
   * «+ instrução» acender antes de a amostra chegar.
   */
  const previstoSalvo = useMemo(() => {
    const m = new Map<string, { previstaId: string; instrucao: string | null }>();
    for (const p of dados?.previstas ?? []) {
      for (const mid of p.metodo_ids) {
        m.set(chave(`prev:${p.id}`, mid),
          { previstaId: p.id, instrucao: p.instrucoes?.[mid] ?? null });
      }
    }
    return m;
  }, [dados]);

  const previsto = useMemo(() => {
    const s = new Set<string>();
    for (const p of dados?.previsto ?? []) s.add(chave(p.amostra_id, p.metodo_id));
    return s;
  }, [dados]);

  const metodoPor = useMemo(() => {
    const m = new Map<string, MetodoDaGrade>();
    for (const x of dados?.metodos ?? []) m.set(x.id, x);
    return m;
  }, [dados]);

  /**
   * O filtro de escopo ESCONDE, e nunca desmarca.
   *
   * Marcar doze células e depois filtrar não pode perder trabalho. É a
   * mesma razão pela qual tirar uma coluna da grade tira as marcas dela
   * de propósito e com aviso, enquanto filtrar não mexe em nada.
   */
  /**
   * O saldo do CONTRATO por método, já contando o que está marcado.
   *
   * «Depois na programação vinculamos os ensaios para cada amostra»
   * (Testesolo, 02/09/2026) — e é aqui que o contrato é gasto. O número
   * não vem só do servidor: ele soma as células marcadas AGORA e
   * desconta as que estão saindo, porque quem está marcando precisa ver
   * o saldo mudar no clique, e não depois de salvar.
   *
   * A trava de verdade é do banco (`fn_ensaio_cabe_no_escopo`, 0149).
   * Este número é o aviso ANTES do gesto; a recusa continua sendo a
   * garantia. Mostrar aqui não substitui a trava — evita que o técnico
   * marque doze células e descubra na hora de salvar.
   */
  const saldoPorMetodo = useMemo(() => {
    const m = new Map<string, {
      contratada: number; noServidor: number; marcadas: number;
    }>();
    for (const c of (dados?.contratado ?? [])) {
      m.set(c.metodo_id, {
        contratada: Number(c.quantidade_contratada),
        noServidor: c.programados,
        marcadas: 0,
      });
    }
    if (m.size === 0) return m;

    /*
     * O que está marcado na tela e AINDA não existe no servidor. As
     * células que já viraram ensaio já estão em `programados`; contá-las
     * de novo dobraria o gasto.
     */
    for (const k of marcadas) {
      const [amostraId, metodoId] = k.split('|');
      if (existente.has(chave(amostraId, metodoId))) continue;
      const linha = m.get(metodoId);
      if (linha) linha.marcadas += 1;
    }
    return m;
  }, [dados, marcadas, existente]);

  /*
   * O que a OS contratou NÃO tem — e o banco vai recusar (0149).
   *
   * UX-023: a barra listava só os métodos contratados; um LA marcado
   * numa OS sem LA no escopo só aparecia na recusa do servidor, depois
   * de trinta cliques. Aqui a lista dos métodos em jogo que estão fora
   * do escopo — para a barra, para o rodapé e para o botão de
   * pré-marcação contarem só o que pode ser programado.
   *
   * Desde a 0220 (`RN-218`) vale também para a amostra «por chegar» — a
   * marca nela reserva vaga no escopo (D6), e o banco a recusa fora dele —
   * e para a OS sem escopo, que não programa (D1): ali tudo está fora.
   */
  const temEscopo = saldoPorMetodo.size > 0;
  const foraDoEscopo = useCallback((metodoId: string) =>
    !saldoPorMetodo.has(metodoId), [saldoPorMetodo]);

  /** As células marcadas (e ainda não gravadas) cujo método está fora do escopo. */
  const marcadasForaDoEscopo = useMemo(() => {
    /* O que já está gravado (ensaio, ou marca em amostra por chegar) não
       é gesto novo: a recusa é só para o que este «Salvar» acrescenta. */
    const salvo = (k: string) => existente.has(k) || previstoSalvo.has(k);
    return [...marcadas].filter((k) => !salvo(k) && foraDoEscopo(k.split('|')[1]));
  }, [marcadas, existente, previstoSalvo, foraDoEscopo]);

  /** 268: o escopo no formato da faixa — o servidor mais o marcado agora. */
  const linhasDoEscopo = useMemo((): LinhaDoEscopo[] =>
    (dados?.contratado ?? []).map((c) => ({
      ...linhaDoServidor({ ...c, metodo_nome: c.metodo_nome ?? metodoPor.get(c.metodo_id)?.nome }),
      /* A grade conta a reserva como célula marcada (previstoSalvo): aqui
         ela entra uma vez só, por `marcadas`. */
      reservados: 0,
      marcadas: saldoPorMetodo.get(c.metodo_id)?.marcadas ?? 0,
    })), [dados, saldoPorMetodo, metodoPor]);

  const metodosForaDoEscopo = useMemo(() => {
    if (!temEscopo) return [] as MetodoDaGrade[];
    const ids = new Set(
      [...marcadas, ...existente.keys()]
        .map((k) => k.split('|')[1]));
    return [...ids].map((id) => metodoPor.get(id))
      .filter((m): m is MetodoDaGrade => !!m && foraDoEscopo(m.id))
      .sort((a, b) => a.codigo.localeCompare(b.codigo, 'pt-BR'));
  }, [marcadas, existente, temEscopo, foraDoEscopo, metodoPor]);

  const noEscopo = useCallback((metodoId: string) => {
    if (ligados.size === 0) return true;
    const m = metodoPor.get(metodoId);
    return !m || ligados.has(m.escopo);
  }, [ligados, metodoPor]);

  const visiveis = useMemo(
    () => (dados?.metodos ?? [])
      .filter((m) => colunas.has(m.id) && noEscopo(m.id)),
    [dados, colunas, noEscopo]);

  /*
   * O que se OFERECE para programar: só o escopo vigente da OS (0220,
   * `RN-218`). «+ acrescentar ensaio», a programação em massa e as
   * baterias deixam de mostrar o catálogo inteiro; o ensaio que não está
   * no escopo entra pela revisão («Incluir na OS»).
   */
  const metodosOferecidos = useMemo(
    () => (dados?.metodos ?? []).filter((m) => m.na_os),
    [dados]);
  const idsOferecidos = useMemo(
    () => new Set(metodosOferecidos.map((m) => m.id)), [metodosOferecidos]);

  const disponiveis = useMemo(
    () => (dados?.amostras ?? []).filter((a) => !FORA.includes(a.status)),
    [dados]);

  /** As amostras que já têm ensaio GRAVADO — o recorte «Sem ensaio» (257). */
  const comEnsaio = useMemo(() => {
    const c = new Set<string>();
    for (const k of [...existente.keys(), ...previstoSalvo.keys()]) c.add(k.split('|')[0]);
    return c;
  }, [existente, previstoSalvo]);

  /** As linhas à vista: o grupo escolhido (0188). */
  const linhas = useMemo(() => {
    const l = (dados?.amostras ?? [])
      .filter((a) => noGrupo(a, grupo, comEnsaio) && casaBusca(a, busca));
    /* Agrupar por furo é ordenar por furo — estável: dentro do furo, a
       ordem de sempre (a da coleta). */
    return porFuro ? [...l].sort((x, y) => ordemDoFuro(furoDe(x), furoDe(y))) : l;
  }, [dados, grupo, comEnsaio, busca, porFuro]);
  /** Quantas linhas cada furo tem — o número do cabeçalho do grupo. */
  const porFuroConta = useMemo(() => {
    const c = new Map<string, number>();
    for (const a of linhas) c.set(furoDe(a), (c.get(furoDe(a)) ?? 0) + 1);
    return c;
  }, [linhas]);
  /** A linha abre um furo novo? (só quando agrupa) */
  const abreFuro = (i: number) => porFuro
    && (i === 0 || furoDe(linhas[i - 1]) !== furoDe(linhas[i]));
  /** As amostras sobre as quais a coluna e a bateria agem: o grupo, sem as fora do acervo. */
  const alvo = useMemo(
    () => disponiveis.filter((a) => noGrupo(a, grupo, comEnsaio) && casaBusca(a, busca)),
    [disponiveis, grupo, comEnsaio, busca]);
  /** Quantas amostras cada grupo tem — o número do chip. */
  const contaPorGrupo = useMemo(() => {
    const c = new Map<Grupo, number>();
    for (const g of GRUPOS) {
      c.set(g.chave, disponiveis.filter((a) => noGrupo(a, g.chave, comEnsaio)).length);
    }
    return c;
  }, [disponiveis, comEnsaio]);

  /** Quantos ensaios cada escopo tem nesta OS — o número do trilho. */
  const contaPorEscopo = useMemo(() => {
    const c = new Map<string, number>();
    for (const k of new Set([...marcadas, ...existente.keys()])) {
      const m = metodoPor.get(k.split('|')[1]);
      if (!m) continue;
      c.set(m.escopo, (c.get(m.escopo) ?? 0) + 1);
    }
    return c;
  }, [marcadas, existente, metodoPor]);

  /**
   * Os métodos EM JOGO nesta OS cuja conta ainda não foi reconhecida.
   *
   * «Em jogo» é o que está marcado ou já programado — e não a grade
   * inteira: avisar sobre um método que ninguém pediu seria ruído, e
   * ruído é como o aviso que importa deixa de ser lido.
   */
  const semConta = useMemo(() => {
    const ids = new Set(
      [...marcadas, ...existente.keys()].map((k) => k.split('|')[1]));
    return [...ids]
      .map((id) => metodoPor.get(id))
      .filter((m): m is NonNullable<typeof m> =>
        !!m && m.sem_versao_calculo)
      .sort((a, b) => a.codigo.localeCompare(b.codigo, 'pt-BR'));
  }, [marcadas, existente, metodoPor]);

  /** Célula que não se mexe: ensaio que já saiu da programação. */
  const travada = useCallback((k: string) => {
    const e = existente.get(k);
    return !!e && INTOCAVEIS.includes(e.status);
  }, [existente]);

  const alternarCelula = (amostraId: string, metodoId: string) => {
    const k = chave(amostraId, metodoId);
    if (travada(k)) return;
    setMarcadas((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  };

  /**
   * A coluna inteira, num clique.
   *
   * «Quase sempre todas iguais» — a resposta do Humberto sobre como os
   * ensaios variam dentro de uma OS. Então o gesto principal é a COLUNA:
   * este ensaio, em todas as amostras.
   */
  const alternarColuna = (metodoId: string) => {
    const alvos = alvo
      .map((a) => chave(a.id, metodoId))
      .filter((k) => !travada(k));
    const todasMarcadas = alvos.length > 0 && alvos.every((k) => marcadas.has(k));
    setMarcadas((s) => {
      const n = new Set(s);
      for (const k of alvos) if (todasMarcadas) n.delete(k); else n.add(k);
      return n;
    });
  };

  const alternarLinha = (amostraId: string) => {
    const alvos = visiveis
      .map((m) => chave(amostraId, m.id))
      .filter((k) => !travada(k));
    const todas = alvos.length > 0 && alvos.every((k) => marcadas.has(k));
    setMarcadas((s) => {
      const n = new Set(s);
      for (const k of alvos) if (todas) n.delete(k); else n.add(k);
      return n;
    });
  };

  /**
   * «Programar nas selecionadas: + COD» (257, §9.3).
   *
   * A ação em lote só existe com seleção. Ela abre a coluna do método e
   * marca as amostras escolhidas — e NÃO passa do saldo do escopo: «o saldo
   * anda ao vivo; o que excede não marca» (§7). O que ficou de fora é dito
   * na hora, com a saída («Incluir na OS»), e não descoberto no salvar.
   */
  const programarNasSelecionadas = (metodoId: string) => {
    const m = metodoPor.get(metodoId);
    const alvos = [...selecionadas]
      .filter((id) => disponiveis.some((a) => a.id === id))
      .map((id) => chave(id, metodoId))
      .filter((k) => !travada(k) && !marcadas.has(k));
    const linha = saldoPorMetodo.get(metodoId);
    const resta = linha ? Math.max(0, linha.contratada - linha.noServidor - linha.marcadas) : alvos.length;
    const entram = alvos.slice(0, resta);
    setColunas((c) => new Set([...c, metodoId]));
    setMarcadas((s) => new Set([...s, ...entram]));
    setSemSaldo(alvos.length > entram.length
      ? `${alvos.length - entram.length} amostra(s) ficaram sem ${m?.codigo ?? 'o ensaio'}: `
        + `o saldo do escopo acabou (${linha?.contratada ?? 0} contratado(s)).`
      : null);
  };

  /** A bateria acrescenta as colunas E marca todas as amostras nelas —
      só os métodos dela que estão no escopo da OS (0220). */
  const aplicarBateria = (b: BateriaDaGrade) => {
    const ids = b.metodo_ids.filter((m) => idsOferecidos.has(m));
    setColunas((c) => new Set([...c, ...ids]));
    setMarcadas((s) => {
      const n = new Set(s);
      for (const a of alvo) {
        for (const m of ids) {
          const k = chave(a.id, m);
          if (!travada(k)) n.add(k);
        }
      }
      return n;
    });
  };

  /** «Programar o que a OS previu» — o caso comum, num clique. */
  /**
   * O que a OS previu e AINDA dá para programar: amostra disponível,
   * célula não travada, ainda não marcada nem existente, e método dentro
   * do escopo quando há escopo (UX-023 — o «(6)» contava três que o
   * servidor ia recusar, e continuava «(6)» depois de salvar).
   */
  const previstoProgramavel = useMemo(() => [...previsto].filter((k) => {
    const [amostraId, metodoId] = k.split('|');
    if (marcadas.has(k) || existente.has(k) || travada(k)) return false;
    if (!disponiveis.some((a) => a.id === amostraId)) return false;
    return !foraDoEscopo(metodoId);
  }), [previsto, marcadas, existente, travada, disponiveis, foraDoEscopo]);

  const marcarOPrevisto = () => {
    setMarcadas((s2) => new Set([...s2, ...previstoProgramavel]));
  };

  const acrescentarColuna = (metodoId: string) => {
    setColunas((c) => new Set([...c, metodoId]));
    setAbrindoBusca(false);
  };

  const removerColuna = (metodoId: string) => {
    setColunas((c) => { const n = new Set(c); n.delete(metodoId); return n; });
    setMarcadas((s) => new Set([...s].filter(
      (k) => k.split('|')[1] !== metodoId)));
  };

  /*
   * O que vai mudar quando alguém salvar.
   *
   * A conta é feita AQUI e mostrada no botão, e não descoberta depois da
   * resposta do servidor: «Salvar (3 novos, 1 excluído)» deixa a pessoa
   * ver o efeito antes de causá-lo, que é o que separa uma exclusão
   * intencional de um clique errado numa grade de oito colunas.
   */
  const diff = useMemo(() => {
    const vivas = new Set([...existente.keys()]);
    const novas = [...marcadas].filter((k) => !vivas.has(k));
    const saindo = [...vivas].filter((k) => !marcadas.has(k) && !travada(k));
    return { novas, saindo };
  }, [marcadas, existente, travada]);

  const mudou = diff.novas.length > 0 || diff.saindo.length > 0;
  const aberta = dados?.programacao.aberta ?? false;

  /*
   * O que dá para reprogramar: o que ainda vai acontecer.
   *
   * Ensaio aprovado, cancelado ou substituído não se reprograma — ele já
   * não vai acontecer de novo. O banco também recusa (`0139`); aqui é
   * para o botão não oferecer o que vai ser negado.
   */
  const idsProgramaveis = (dados?.programado ?? [])
    .filter((p) => !['aprovado', 'cancelado', 'substituido'].includes(p.status))
    .map((p) => p.id);
  const podeReprogramar = idsProgramaveis.length > 0;

  const enviar = async () => {
    setEnviando(true); setErro(null); setFeito(null);
    try {
      const todas = [...marcadas].map((k) => {
        const [amostraId, metodoId] = k.split('|');
        return { amostraId, metodoId };
      });
      const r = await salvarGrade(dados!.os.numero, {
        celulas: todas.filter((c) => !c.amostraId.startsWith('prev:')),
        previstas: todas.filter((c) => c.amostraId.startsWith('prev:'))
          .map((c) => ({ previstaId: c.amostraId.slice(5), metodoId: c.metodoId })),
        dataPrevista: dataPrevista || undefined,
      });

      /*
       * A instrução vem DEPOIS da programação, e é por isso que ela é
       * aplicada aqui e não junto.
       *
       * `ensaio.instrucao` só existe quando o ensaio existe. Recarregar
       * primeiro e escrever depois é o que faz «programar doze
       * adensamentos com a mesma receita» ser um gesto só para quem usa
       * a tela, mesmo sendo dois no servidor.
       */
      const g = await gradeDaOs(dados!.os.numero);
      setDados(g);
      let comInstrucao = 0;
      if (instrucaoPendente.size > 0) {
        const porTexto = new Map<string, string[]>();
        for (const e of g.programado) {
          const texto = instrucaoPendente.get(e.metodo_id);
          if (!texto) continue;
          if (!marcadas.has(chave(e.amostra_id, e.metodo_id))) continue;
          if (!porTexto.has(texto)) porTexto.set(texto, []);
          porTexto.get(texto)!.push(e.id);
        }
        for (const [texto, ids] of porTexto) {
          await gravarInstrucao({ ensaioIds: ids, instrucao: texto });
          comInstrucao += ids.length;
        }
        setInstrucaoPendente(new Map());
      }

      const partes = [
        r.criados ? `${r.criados} ensaio(s) programado(s)` : null,
        r.cancelados ? `${r.cancelados} excluído(s) da programação` : null,
        (r as any).previstos ? `${(r as any).previstos} marcado(s) em amostra que ainda vai chegar` : null,
        (r as any).previstosRetirados ? `${(r as any).previstosRetirados} desmarcado(s)` : null,
        comInstrucao ? `${comInstrucao} com instrução` : null,
      ].filter(Boolean);
      setFeito(partes.length ? `${partes.join(' · ')}.` : 'Programação confirmada.');
      setConfirmando(false);
      if (r.criados) setVezEtq((v) => v + 1);
      await carregar();
      /* O cabeçalho, a régua e as abas contam o que acabou de mudar. */
      aoSalvar?.();
    } catch (e) { setErro(e); setConfirmando(false); }
    finally { setEnviando(false); }
  };

  /* O que está marcado, separado entre amostra que chegou e «por chegar»:
     a frase do rodapé não pode chamar de «programado» o que ainda é
     previsão (0161). */
  const totalReal = [...marcadas].filter((k) => !k.startsWith('prev:')).length;
  const totalPrev = marcadas.size - totalReal;

  if (carregando && !dados) return <Carregando texto="Abrindo a programação" />;
  if (!dados) return <ErroDaApi erro={erro} />;

  /** Os ensaios de uma amostra, para a sanfona — marcados e existentes. */
  const ensaiosDa = (amostraId: string) => (dados.metodos)
    .filter((m) => noEscopo(m.id))
    .filter((m) => marcadas.has(chave(amostraId, m.id))
      || existente.has(chave(amostraId, m.id)))
    .map((m) => ({ metodo: m, ja: existente.get(chave(amostraId, m.id)) }));

  /**
   * O que se lê de UMA amostra: os ensaios dela, a situação de cada um, a
   * instrução e a saída da que não vai chegar. Era o corpo da sanfona;
   * desde a 257 é também a ficha à direita da grade (§9.3).
   */
  const corpoDa = (a: AmostraDaGrade) => {
    const fora = FORA.includes(a.status);
    const ens = ensaiosDa(a.id);
    return (
      <div className="am-corpo">

        {a.prevista && (
          <p className="linha-ensaio acoes-prevista">
            <button type="button" className="ligacao perigo excluir-prevista"
              title="Esta amostra não vai chegar — excluir da OS"
              onClick={() => setExcluir({
                id: a.id.replace(/^prev:/, ''),
                nome: a.identificacao_recebida,
                ensaios: ens.length,
              })}>
              Excluir esta amostra por chegar
            </button>
          </p>
        )}
        {fora && (
          <p className="linha-ensaio aviso-linha">
            {rotulo(a.status)} — não recebe ensaio novo.
          </p>
        )}
        {ens.length === 0 ? (
          <p className="vazio-am">
            Nenhum ensaio nesta amostra
            {ligados.size < escopos.length
              ? ' nos escopos ligados.' : '.'}
          </p>
        ) : ens.map(({ metodo, ja }) => (
          <div key={metodo.id}
            className={`linha-ensaio escopo-${metodo.escopo}`}>
            <span className="pt" aria-hidden="true" />
            <span className="cod mono">{metodo.codigo}</span>
            <span className="nm">{metodo.nome}</span>
            <span className="escopo-nome">{metodo.escopo_nome}</span>
            {ja ? (
              <Link to={`/ensaio/${ja.id}`} className="pill-ensaio">
                <SeloDoEnsaio status={ja.status} />
              </Link>
            ) : (
              <span className="pill novo">a programar</span>
            )}
            {(() => {
              const pv = ja ? undefined
                : previstoSalvo.get(chave(a.id, metodo.id));
              const tem = !!(ja?.instrucao ?? pv?.instrucao);
              const pode = !!(ja || pv);
              return (
                <button type="button"
                  className={`chip-instr${tem ? ' tem' : ''}`}
                  aria-disabled={!pode}
                  title={ja
                    ? 'Instrução técnica deste ensaio'
                    : pv
                      ? 'Instrução técnica — a amostra ainda não chegou; o ensaio nasce com ela'
                      : 'Grave a programação antes de escrever a instrução'}
                  onClick={() => {
                    if (ja) {
                      setGaveta({
                        metodo, ensaios: [{ id: ja.id, amostra: nomeDaAmostra(nomeNaGrade(a)) }],
                        status: ja.status, texto: ja.instrucao ?? '',
                      });
                    } else if (pv) {
                      setGaveta({
                        metodo, ensaios: [],
                        previstos: [{ previstaId: pv.previstaId,
                          metodoId: metodo.id, amostra: rotuloDaPrevista(a) }],
                        status: 'programado', texto: pv.instrucao ?? '',
                      });
                    }
                  }}>
                  {tem ? '✓ instrução' : '+ instrução'}
                </button>
              );
            })()}
            {!ja && (
              <button type="button" className="tirar-celula"
                title={`Tirar ${metodo.codigo} de ${nomeDaAmostra(nomeNaGrade(a))}`}
                onClick={() => alternarCelula(a.id, metodo.id)}>×</button>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="programar">
      {/* 257: o número, o cliente e a obra estão no cabeçalho da OS, logo
          acima; aqui fica só o que é desta aba. */}
      <div className="cabeca-aba-prog">
        <h2>Programação de Amostras e Ensaios</h2>
        <p className="sub">
          {dados.amostras.filter((a) => !a.prevista).length} recebida(s) ·{' '}
          {dados.amostras.filter((a) => a.prevista).length} por chegar ·{' '}
          {/* 332 (D1): a marca gravada em amostra por chegar é programação. */}
          {existente.size + previstoSalvo.size} ensaio(s) programado(s)
          {previstoSalvo.size > 0 && ` (${previstoSalvo.size} por chegar)`}
        </p>
      </div>

      {/*
        * As amostras entram por aqui — antes ou depois de chegar (0161).
        * Uma amostra que ainda vai chegar é uma linha da grade; quando
        * chega, o recebimento a risca e os ensaios marcados nela nascem
        * programados.
        */}
      <div className="acoes acoes-prog">
        <button type="button" className="botao secundario"
          onClick={() => setNovaAmostra('serie')}>
          + Amostras em série
        </button>
        <button type="button" className="botao secundario"
          onClick={() => setNovaAmostra('uma')}>
          + Uma amostra
        </button>
        <Link className="botao secundario" to={`/recebimento/novo?os=${dados.os.id}`}>
          Receber amostras
        </Link>
        {podeReprogramar && (
          <button type="button" className="botao discreto"
            onClick={() => setReprogramando(true)}>
            Reprogramar a OS
          </button>
        )}
      </div>
      {novaAmostra === 'serie' && (
        <FormProgramar os={{ id: dados.os.id } as any}
          aoFechar={() => setNovaAmostra(null)}
          aoPronto={() => { setNovaAmostra(null); void carregar(); }} />
      )}
      {excluir && (
        <FormExcluirPrevista osId={dados.os.id} prevista={excluir}
          aoFechar={() => setExcluir(null)}
          aoPronto={() => { setExcluir(null); void carregar(); }} />
      )}
      {novaAmostra === 'uma' && (
        <FormAmostraPrevista os={{ id: dados.os.id } as any}
          aoFechar={() => setNovaAmostra(null)}
          aoPronto={() => { setNovaAmostra(null); void carregar(); }} />
      )}

      {reprogramando && (
        <DialogoReprogramar
          ensaioIds={idsProgramaveis}
          aoFechar={() => setReprogramando(false)}
          aoPronto={(n) => {
            setReprogramando(false);
            setFeito(`${n} ensaio(s) reprogramado(s).`);
            void carregar();
          }}
        />
      )}

      {erro ? <ErroDaApi erro={erro} /> : null}
      {feito && !erro && (
        <Aviso tipo="ok" titulo="Programação salva">
          {feito}{' '}
          {/* 257: o próximo passo é agendar — máquina e data (§9.2). */}
          <Link to="/cronograma">Ver no cronograma →</Link>
          {' · '}<Link to="/fila">ver a fila do laboratório</Link>
        </Aviso>
      )}
      {/* 287: as amostras que ganharam ensaio e cuja etiqueta saiu sem ele. */}
      {feito && !erro && (
        <EtiquetasDepoisDaProgramacao osId={dados.os.id} osNumero={dados.os.numero} vez={vezEtq} />
      )}

      {!aberta && (
        <Aviso tipo="atencao" titulo="OS cancelada">{dados.programacao.diz}</Aviso>
      )}

      {/* D1 (0220, RN-218): OS sem ensaio no escopo não programa. */}
      {aberta && !temEscopo && (
        <Aviso tipo="atencao" titulo="Esta OS ainda não tem ensaio no escopo">
          <p style={{ margin: '0 0 .5rem' }}>
            É o escopo que libera a programação (RN-218): só se programa o
            ensaio que a OS contratou. As amostras podem ser previstas sem
            ensaio; para marcar ensaio, lance antes o escopo da OS.
          </p>
          <Link className="botao secundario" to={`/os/${dados.os.id}?escopo=1`}>
            Lançar o escopo da OS
          </Link>
        </Aviso>
      )}

      {semConta.length > 0 && (
        <Aviso tipo="atencao"
          titulo="Ensaio que não vai poder ser aprovado">
          <p>
            {semConta.map((m) => m.codigo).join(', ')}
            {semConta.length === 1
              ? ' ainda não tem conta reconhecida'
              : ' ainda não têm conta reconhecida'}
            . Programar e executar funciona; o clique de <em>aprovar</em> é
            que vai ser recusado (RN-121) — e recusado no fim, com o laudo
            pronto.
          </p>
          <p className="sub">
            Quem resolve é o responsável técnico, em{' '}
            <Link to="/calculos">Métodos homologados</Link>. Leva um minuto e
            vale para todos os ensaios daquele método, hoje e depois.
          </p>
        </Aviso>
      )}

      {/* ── o trilho de escopos ──────────────────────────────────── */}
      <section className="escopos-da-os" role="group"
        aria-label="Filtrar por escopo">
        {escopos.map((e) => {
          const n = contaPorEscopo.get(e.codigo) ?? 0;
          if (n === 0) return null;
          const on = ligados.has(e.codigo);
          return (
            <button key={e.codigo} type="button"
              className={`escopo-tag escopo-${e.codigo}`}
              aria-pressed={on}
              title={on ? `Esconder ${e.nome}` : `Mostrar ${e.nome}`}
              onClick={() => setLigados((s) => {
                const x = new Set(s);
                if (x.has(e.codigo)) x.delete(e.codigo); else x.add(e.codigo);
                return x;
              })}>
              <span className="pt" aria-hidden="true" />
              {e.nome}
              <span className="n mono">{n}</span>
            </button>
          );
        })}
      </section>

      {/*
        * ── os grupos (0188) ─────────────────────────────────────────
        *
        * Antes das ferramentas, porque é o que elas obedecem: escolher
        * «Indeformadas» e apertar uma bateria marca a bateria SÓ nas
        * indeformadas. O chip só aparece quando o grupo tem alguém —
        * uma OS só de deformadas não precisa de dois chips vazios.
        */}
      {disponiveis.length > 0 && (
        <section className="grupos-da-os" role="group"
          aria-label="Amostras à vista">
          <div className="recortes-prog">
            {RECORTES.map((g) => {
              const n = contaPorGrupo.get(g.chave) ?? 0;
              return (
                <button key={g.chave} type="button" className="chip-grupo recorte"
                  aria-pressed={grupo === g.chave}
                  onClick={() => setGrupo(g.chave)}>
                  {g.nome} <span className="n mono">{n}</span>
                </button>
              );
            })}
          </div>
          {/* O tipo só aparece quando a OS tem mais de um (0188, 0209). */}
          {TIPOS.filter((g) => (contaPorGrupo.get(g.chave) ?? 0) > 0).length > 1 && (
            <div className="tipos-prog" role="group" aria-label="Tipo de amostra">
              <span className="rot">Tipo</span>
              {TIPOS.map((g) => {
                const n = contaPorGrupo.get(g.chave) ?? 0;
                if (n === 0) return null;
                return (
                  <button key={g.chave} type="button" className="chip-grupo"
                    aria-pressed={grupo === g.chave}
                    onClick={() => setGrupo(grupo === g.chave ? 'todas' : g.chave)}>
                    {g.nome} <span className="n mono">{n}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="busca-prog">
            <label className="busca-amostra">
              <span className="só-leitor">Buscar amostra</span>
              <input type="search" value={busca}
                placeholder="Buscar amostra, furo, camada…"
                aria-label="Buscar amostra, furo ou camada"
                onChange={(e) => setBusca(e.target.value)} />
            </label>
            <label className="agrupar-furo">
              <input type="checkbox" checked={porFuro}
                onChange={(e) => setPorFuro(e.target.checked)} />
              Agrupar por furo
            </label>
          </div>
          {(grupo !== 'todas' || busca.trim() !== '') && (
            <span className="sub">
              coluna e bateria marcam só as {alvo.length} amostra(s) {busca.trim()
                ? 'deste recorte e desta busca' : 'deste recorte'} — o resto não muda
            </span>
          )}
        </section>
      )}

      {/* ── as ferramentas ───────────────────────────────────────── */}
      <section className="bloco atalhos-programacao">
        <div className="seg" role="group" aria-label="Modo de visualização">
          <button type="button" aria-pressed={modo === 'sanfona'}
            onClick={() => setModo('sanfona')}>Sanfona</button>
          <button type="button" aria-pressed={modo === 'grade'}
            onClick={() => setModo('grade')}>Grade</button>
        </div>

        {metodosOferecidos.length > 0 && (
          <button type="button" className="principal-leve"
            onClick={() => setMassa(true)}>+ Programar em massa</button>
        )}

        {(existente.size > 0 || previstoSalvo.size > 0) && (
          <button type="button"
            title="Uma instrução para o mesmo ensaio em várias amostras desta OS — recebidas ou ainda por chegar"
            onClick={() => setInstruirVarias(true)}>
            Instrução para várias amostras
          </button>
        )}

        {previsto.size > 0 && (
          <button type="button"
            onClick={() => {
              if (previstoProgramavel.length === 0) return;
              marcarOPrevisto();
            }}
            aria-disabled={previstoProgramavel.length === 0}
            title={previstoProgramavel.length === 0
              ? 'Tudo o que a OS previu e cabe no escopo já está marcado.'
              : `${previstoProgramavel.length} de ${previsto.size} previsto(s) ainda por marcar`
                + (temEscopo ? ' — fora do escopo não conta' : '')}>
            Marcar o que a OS previu ({previstoProgramavel.length})
          </button>
        )}

        {dados.baterias
          .filter((b) => b.metodo_ids.some((m) => idsOferecidos.has(m)))
          .map((b) => (
            <button key={b.id} type="button" title={b.descricao ?? undefined}
              onClick={() => aplicarBateria(b)}>
              {b.nome.toUpperCase()}
            </button>
          ))}

        {/* 257: o escopo já abre como coluna; a busca só volta a ter o que
            fazer quando alguém tirou uma coluna da grade. */}
        {(abrindoBusca || metodosOferecidos.some((m) => !colunas.has(m.id))) && (
          <button type="button" className="discreto"
            onClick={() => setAbrindoBusca((v) => !v)}
            aria-expanded={abrindoBusca}>
            {abrindoBusca ? 'fechar' : '+ acrescentar ensaio do escopo'}
          </button>
        )}

        {/* 0220: o que não está no escopo entra pela OS, não pela grade.
            Sem escopo nenhum, o aviso do topo já leva a «Lançar o escopo». */}
        {temEscopo && (
          <Link className="incluir-na-os-link" to={`/os/${dados.os.id}?escopo=1`}>
            Ensaio fora do escopo? Incluir na OS
          </Link>
        )}

        {modo === 'sanfona' && (
          <button type="button" className="discreto"
            onClick={() => setAbertas((s) => (
              linhas.every((a) => s.has(a.id))
                ? new Set()
                : new Set(linhas.map((a) => a.id))))}>
            {linhas.length > 0 && linhas.every((a) => abertas.has(a.id))
              ? 'recolher todas' : 'expandir todas'}
          </button>
        )}

        {marcadas.size > 0 && (
          <button type="button" className="discreto"
            onClick={() => setMarcadas(new Set())}>limpar seleção</button>
        )}
      </section>

      {abrindoBusca && (
        <BuscarMetodo metodos={metodosOferecidos} jaVisiveis={colunas}
          aoEscolher={acrescentarColuna} />
      )}

      {/*
        * O saldo do contrato, acima da grade e nos DOIS modos.
        *
        * O cabeçalho da coluna também mostra, mas a sanfona é o modo
        * padrão e ela não tem cabeçalho de coluna — deixar o saldo só lá
        * seria escondê-lo justamente de quem abre a tela e começa a
        * marcar. Aqui ele fica no caminho, sempre.
        *
        * Não aparece quando a OS não tem escopo: aí programar é livre
        * (0149), e uma faixa vazia dizendo nada só ocuparia espaço.
        */}
      {saldoPorMetodo.size > 0 && (
        <FaixaDoEscopo compacta linhas={linhasDoEscopo} fora={metodosForaDoEscopo}
          rodape={<>
            marcado em amostra por chegar já desconta do saldo; acima do
            contratado, ou método fora dele, é recusado —{' '}
            <Link to={`/os/${dados.os.id}?escopo=1`}>incluir na OS</Link>
            {' '}(revisão)
          </>} />
      )}

      {dados.amostras.length === 0 ? (
        <Vazio>
          Esta OS ainda não tem amostra. Comece por <b>+ Amostras em série</b>
          {' '}(quantas vão chegar, de qual furo, quais ensaios) — ou registre
          o recebimento quando o material chegar sem aviso. Cada amostra vira
          uma linha aqui, e os ensaios se marcam por amostra, antes ou depois
          de ela chegar.
        </Vazio>
      ) : modo === 'sanfona' ? (
        <div className="sanfona-amostras">
          {linhas.length === 0 && (
            <Vazio>{busca.trim()
              ? `Nenhuma amostra casa com «${busca.trim()}» neste grupo.`
              : 'Nenhuma amostra neste grupo. Escolha outro chip acima.'}</Vazio>
          )}
          {linhas.map((a, i) => {
            const fora = FORA.includes(a.status);
            const ens = ensaiosDa(a.id);
            const aberto = abertas.has(a.id);
            return (
              <Fragment key={a.id}>
              {abreFuro(i) && (
                <h3 className="furo-cab">
                  {furoDe(a) || 'Sem furo'}{' '}
                  <span className="n mono">{porFuroConta.get(furoDe(a))} amostra(s)</span>
                </h3>
              )}
              <div
                className={`amostra-bloco${aberto ? '' : ' fechada'}${
                  fora ? ' descartada' : ''}`}>
                <button type="button" className="am-cab" aria-expanded={aberto}
                  onClick={() => setAbertas((s) => {
                    const n = new Set(s);
                    if (n.has(a.id)) n.delete(a.id); else n.add(a.id);
                    return n;
                  })}>
                  <span className="seta" aria-hidden="true">▶</span>
                  <span className={a.prevista ? 'por-chegar' : undefined}>
                    <NomeDaAmostra amostra={nomeNaGrade(a)} emLinha />
                  </span>
                  <span className="det">
                    {[a.ponto_investigacao,
                      faixaProfundidade(a.profundidade_inicial,
                        a.profundidade_final)
                      && `${faixaProfundidade(a.profundidade_inicial,
                        a.profundidade_final)} m`,
                    ].filter(Boolean).join(' · ') || '—'}
                  </span>
                  {/*
                    * O cabeçalho diz o estado do bloco FECHADO.
                    *
                    * É a lição da Suporte Infra e a `CA-29` aplicada a
                    * acordeão: dá para saber o que falta sem abrir nada.
                    */}
                  <span className="cont">
                    <span className="pontos" aria-hidden="true">
                      {ens.map(({ metodo }) => (
                        <i key={metodo.id} className={`escopo-${metodo.escopo}`}
                          title={metodo.codigo} />
                      ))}
                    </span>
                    <span className="qt mono">{ens.length} ensaio(s)</span>
                  </span>
                </button>

                {aberto && corpoDa(a)}
              </div>
              </Fragment>
            );
          })}
        </div>
      ) : (
        <>
          {visiveis.length === 0 && (
            <Aviso tipo="info">
              Nenhum ensaio à vista. Use uma bateria acima, «acrescentar
              ensaio», ou ligue um escopo no filtro.
            </Aviso>
          )}
          {/* 257: a ação em lote só aparece com seleção (§9.3). */}
          {selecionadas.size > 0 && (
            <div className="lote-prog" role="region" aria-label="Ação nas amostras selecionadas">
              <strong>{selecionadas.size} selecionada(s)</strong>
              {metodosOferecidos.length > 0 ? (
                <>
                  <span>· Programar nas selecionadas:</span>
                  {metodosOferecidos.map((m) => {
                    const l = saldoPorMetodo.get(m.id);
                    const resta = l ? l.contratada - l.noServidor - l.marcadas : null;
                    return (
                      <button key={m.id} type="button" className="botao-lote"
                        aria-disabled={resta !== null && resta <= 0}
                        title={`${m.nome}${resta !== null ? ` — saldo ${Math.max(0, resta)}` : ''}`}
                        onClick={() => {
                          if (resta !== null && resta <= 0) {
                            setSemSaldo(`${m.codigo} não tem mais saldo no escopo: inclua na OS (revisão).`);
                            return;
                          }
                          programarNasSelecionadas(m.id);
                        }}>
                        + {m.codigo}
                      </button>
                    );
                  })}
                </>
              ) : (
                <span className="sub">· a OS ainda não tem ensaio no escopo</span>
              )}
              <button type="button" className="botao-lote discreto"
                onClick={() => setSelecionadas(new Set())}>Limpar seleção</button>
            </div>
          )}
          {semSaldo && (
            <p className="sub aviso-linha sem-saldo-prog">
              {semSaldo}{' '}
              <Link to={`/os/${dados.os.id}?escopo=1`}>Incluir na OS</Link>
            </p>
          )}
          <div className={`prog-corpo${ficha ? ' com-ficha' : ''}`}>
          <div className="rolagem-lateral">
            <table className="dados grade-programacao">
              <thead>
                <tr>
                  <th scope="col">
                    {(() => {
                      const escolhiveis = linhas.filter((a) => !FORA.includes(a.status));
                      const todas = escolhiveis.length > 0
                        && escolhiveis.every((a) => selecionadas.has(a.id));
                      return (
                        <input type="checkbox" className="sel-amostra"
                          aria-label="Selecionar todas as amostras à vista"
                          checked={todas} disabled={escolhiveis.length === 0}
                          onChange={() => setSelecionadas(
                            todas ? new Set() : new Set(escolhiveis.map((a) => a.id)))} />
                      );
                    })()}
                    Amostra
                  </th>
                  {visiveis.map((m) => (
                    <th key={m.id} scope="col"
                      className={`col-metodo escopo-${m.escopo}`}>
                      <button type="button" className="cabeca-metodo"
                        title={`${m.nome} (${m.escopo_nome}) — marcar/desmarcar a coluna `
                          + `nas ${alvo.length} amostra(s) à vista`}
                        onClick={() => alternarColuna(m.id)}>
                        {m.codigo}
                        {/*
                          * O nome sob a sigla (0188). «CBR.5» e «M.ESP»
                          * são claros para quem decorou; a grade é a tela
                          * em que quem NÃO decorou marca trinta linhas.
                          */}
                        <span className="nome-metodo">{m.nome}</span>
                      </button>
                      {m.sem_versao_calculo && (
                        <span className="sem-conta" aria-label="sem conta reconhecida"
                          title={`${m.codigo} não tem conta reconhecida — o `
                            + 'ensaio não poderá ser aprovado (RN-121)'}>∑?</span>
                      )}
                      {/*
                        * O saldo do CONTRATO, na coluna do método.
                        *
                        * «3/4» com o número que muda no clique. O ponto
                        * não é decorar: é que o excedente apareça no
                        * gesto que o cria, e não três semanas depois na
                        * medição. Vazio quando a OS não tem escopo — e
                        * aí programar é livre (0149).
                        */}
                      {(() => {
                        const l = linhasDoEscopo.find((x) => x.id === m.id);
                        return l ? <SaldoDoMetodo l={l} /> : null;
                      })()}
                      <button type="button" className="tirar-coluna"
                        title={`Tirar ${m.codigo} da grade`}
                        aria-label={`Tirar ${m.codigo} da grade`}
                        onClick={() => removerColuna(m.id)}>×</button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {linhas.length === 0 && (
                  <tr><td colSpan={visiveis.length + 1} className="sub">
                    {busca.trim()
                      ? `Nenhuma amostra casa com «${busca.trim()}» neste grupo.`
                      : 'Nenhuma amostra neste grupo. Escolha outro chip acima.'}
                  </td></tr>
                )}
                {linhas.map((a, i) => {
                  const fora = FORA.includes(a.status);
                  const doFuro = linhas.filter((x) => furoDe(x) === furoDe(a)
                    && !FORA.includes(x.status));
                  return (
                    <Fragment key={a.id}>
                    {abreFuro(i) && (
                      <tr className="furo-linha">
                        <th scope="rowgroup" colSpan={visiveis.length + 1}>
                          <span className="furo-nome">{furoDe(a) || 'Sem furo'}</span>{' '}
                          <span className="n mono">{porFuroConta.get(furoDe(a))} amostra(s)</span>
                          {doFuro.length > 0 && (
                            <button type="button" className="sel-furo"
                              onClick={() => setSelecionadas((sel) => {
                                const n = new Set(sel);
                                const todas = doFuro.every((x) => n.has(x.id));
                                for (const x of doFuro) {
                                  if (todas) n.delete(x.id); else n.add(x.id);
                                }
                                return n;
                              })}>
                              {doFuro.every((x) => selecionadas.has(x.id))
                                ? 'Desmarcar o furo' : 'Selecionar o furo'}
                            </button>
                          )}
                        </th>
                      </tr>
                    )}
                    <tr className={[fora ? 'descartada' : '',
                      selecionadas.has(a.id) ? 'selecionada' : '',
                      ficha === a.id ? 'na-ficha' : ''].filter(Boolean).join(' ') || undefined}>
                      <th scope="row">
                        <input type="checkbox" className="sel-amostra"
                          aria-label={`Selecionar ${a.prevista && !a.identificacao_recebida ? 'a amostra por chegar' : nomeDaAmostra(nomeNaGrade(a))}`}
                          checked={selecionadas.has(a.id)} disabled={fora}
                          onChange={() => setSelecionadas((sel) => {
                            const n = new Set(sel);
                            if (n.has(a.id)) n.delete(a.id); else n.add(a.id);
                            return n;
                          })} />
                        {/* 257: clicar na amostra abre a ficha à direita. */}
                        <button type="button" className="cabeca-amostra"
                          aria-expanded={ficha === a.id}
                          title="Abrir a ficha da amostra — ensaios, situação e instrução"
                          onClick={() => setFicha(ficha === a.id ? null : a.id)}>
                          {/* 268 + RN-248 (onda 303): a célula canônica, invertida —
                              o nome do cliente em destaque, o código embaixo;
                              furo · profundidade · tipo na linha menor. */}
                          {a.prevista && !a.identificacao_recebida
                            ? '○ Por chegar' : <NomeDaAmostra amostra={nomeNaGrade(a)} />}
                        </button>
                        <span className="sub">
                          {[a.ponto_investigacao,
                            faixaProfundidade(a.profundidade_inicial,
                              a.profundidade_final)
                            && `${faixaProfundidade(a.profundidade_inicial,
                              a.profundidade_final)} m`,
                            a.tipo_amostra === 'deformada' ? 'deformada'
                              : a.tipo_amostra === 'indeformada' ? 'indeformada'
                                : a.tipo_amostra === 'cp_moldado' ? 'CP moldado' : null,
                            a.coordenada_prevista ?? null,
                          ].filter(Boolean).join(' · ') || '—'}
                        </span>
                        {fora && (
                          <span className="sub aviso-linha">
                            {rotulo(a.status)} — não recebe ensaio novo
                          </span>
                        )}
                      </th>

                      {visiveis.map((m) => {
                        const k = chave(a.id, m.id);
                        const ja = existente.get(k);
                        return (
                          <td key={m.id} className={`celula-grade escopo-${m.escopo}`}>
                            <Celula
                              marcada={marcadas.has(k)}
                              previsto={previsto.has(k)}
                              ja={ja} fora={fora} travada={travada(k)}
                              temInstrucao={!!(ja?.instrucao
                                ?? previstoSalvo.get(k)?.instrucao)}
                              rotulo={`${m.codigo} para ${nomeDaAmostra(nomeNaGrade(a))}`}
                              aoAlternar={() => alternarCelula(a.id, m.id)}
                              aoInstruir={ja ? () => setGaveta({
                                metodo: m,
                                ensaios: [{ id: ja.id, amostra: nomeDaAmostra(nomeNaGrade(a)) }],
                                status: ja.status, texto: ja.instrucao ?? '',
                              }) : previstoSalvo.has(k) ? () => setGaveta({
                                metodo: m, ensaios: [],
                                previstos: [{
                                  previstaId: previstoSalvo.get(k)!.previstaId,
                                  metodoId: m.id, amostra: rotuloDaPrevista(a) }],
                                status: 'programado',
                                texto: previstoSalvo.get(k)!.instrucao ?? '',
                              }) : undefined} />
                          </td>
                        );
                      })}
                    </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {ficha && (() => {
            const a = dados.amostras.find((x) => x.id === ficha);
            if (!a) return null;
            const fora = FORA.includes(a.status);
            return (
              <aside className="ficha-amostra-prog" aria-label="Ficha da amostra">
                <header>
                  <div>
                    <h3><NomeDaAmostra amostra={nomeNaGrade(a)} forte={false} /></h3>
                    <p className="sub">
                      {[a.ponto_investigacao,
                        faixaProfundidade(a.profundidade_inicial, a.profundidade_final)
                          && `${faixaProfundidade(a.profundidade_inicial, a.profundidade_final)} m`,
                        a.tipo_amostra === 'cp_moldado' ? 'CP moldado' : a.tipo_amostra,
                      ].filter(Boolean).join(' · ') || '—'}
                    </p>
                    <p className="sub">
                      {a.prevista ? '○ Por chegar — o ensaio marcado nasce quando ela chegar'
                        : `Situação: ${rotulo(a.status)}`}
                    </p>
                  </div>
                  <button type="button" className="fechar-ficha" aria-label="Fechar a ficha"
                    onClick={() => setFicha(null)}>×</button>
                </header>
                {!fora && visiveis.length > 0 && (
                  <button type="button" className="botao-lote"
                    onClick={() => alternarLinha(a.id)}>
                    Marcar/desmarcar os {visiveis.length} ensaio(s) da grade nesta amostra
                  </button>
                )}
                {corpoDa(a)}
              </aside>
            );
          })()}
          </div>
          <p className="legenda-grade">
            <span className="marca-previsto" aria-hidden="true" />{' '}
            previsto no escopo da OS · <span className="marca-feito">◷</span>{' '}
            já em bancada (não se desmarca) · o anel diz que a célula tem
            instrução
          </p>
        </>
      )}

      {gaveta && (
        <GavetaDeInstrucao alvo={gaveta} aoFechar={() => setGaveta(null)}
          aoGravar={async () => { setGaveta(null); await carregar(); }} />
      )}

      {instruirVarias && (
        <InstruirVarias
          dados={dados} disponiveis={disponiveis} existente={existente}
          previstoSalvo={previstoSalvo}
          aoFechar={() => setInstruirVarias(false)}
          aoEscolher={(alvo) => { setInstruirVarias(false); setGaveta(alvo); }} />
      )}

      {massa && (
        <ProgramarEmMassa
          dados={{ ...dados, metodos: metodosOferecidos }}
          disponiveis={disponiveis} existente={existente}
          aoFechar={() => setMassa(false)}
          aoAplicar={(celulas, metodoIds, instrucao) => {
            setColunas((c) => new Set([...c, ...metodoIds]));
            setMarcadas((s) => new Set([...s, ...celulas]));
            if (instrucao.trim()) {
              setInstrucaoPendente((m) => {
                const n = new Map(m);
                for (const id of metodoIds) n.set(id, instrucao.trim());
                return n;
              });
            }
            setMassa(false);
          }} />
      )}

      {/* ── salvar ──────────────────────────────────────────────── */}
      {/*
        * A barra de salvar fica GRUDADA no rodapé (0188). Com trinta
        * sanfonas, «Confirmar a programação» ficava a uma tela inteira do
        * último clique; agora o resumo do que muda e o botão acompanham a
        * rolagem — é a mesma `acoes-fixas` do assistente de recebimento.
        */}
      {dados.amostras.length > 0 && (
      <section className="bloco confirmar confirmar-fixa barra-prog">
        {/* 257: a barra de rodapé do Carbon — o que muda, quando começa,
            descartar e salvar (§9.1, «Barra de ação fixa»). */}
        <p className="barra-prog-resumo">
          {mudou
            ? <strong>{diff.novas.length} ensaio(s) novo(s) para salvar</strong>
            : <span className="sub">Nada novo para salvar — marque na grade ou selecione amostras.</span>}
        </p>
        <div className="form linha">
          <Campo rotulo="Início previsto" type="date" value={dataPrevista}
            onChange={(e) => setDataPrevista(e.target.value)} />
        </div>

        {marcadasForaDoEscopo.length > 0 && (
          <p className="sub aviso-linha">
            {marcadasForaDoEscopo.length === 1
              ? 'Uma célula marcada está fora do escopo contratado e vai ser recusada: '
              : `${marcadasForaDoEscopo.length} células marcadas estão fora do escopo contratado e vão ser recusadas: `}
            {marcadasForaDoEscopo.map((k) => {
              const [amostraId, metodoId] = k.split('|');
              const a = dados.amostras.find((x) => x.id === amostraId);
              return `${a ? nomeDaAmostra(nomeNaGrade(a)) : '?'}/${metodoPor.get(metodoId)?.codigo ?? '?'}`;
            }).join(', ')}
            . Desmarque, ou{' '}
            <Link to={`/os/${dados.os.id}?escopo=1`}>inclua o ensaio na OS</Link>
            {' '}(revisão).{' '}
            <button type="button" className="como-link"
              onClick={() => setMarcadas((s2) => {
                const n = new Set(s2);
                for (const k of marcadasForaDoEscopo) n.delete(k);
                return n;
              })}>
              Tirar as que estão fora do escopo
            </button>
          </p>
        )}

        {mudou && (
          <p className="resumo-diff">
            {diff.novas.length > 0 && (
              <span className="entra">+{diff.novas.length} ensaio(s)</span>
            )}
            {diff.saindo.length > 0 && (
              <span className="sai">−{diff.saindo.length} excluído(s) da
                programação</span>
            )}
            {instrucaoPendente.size > 0 && (
              <span className="sub">
                {instrucaoPendente.size} instrução(ões) a gravar depois
              </span>
            )}
            <span className="sub">
              {totalReal > 0 && `${totalReal} programado(s)`}
              {totalReal > 0 && totalPrev > 0 && ' · '}
              {totalPrev > 0 && `${totalPrev} previsto(s) para quando chegar`}
              {' '}depois de salvar
            </span>
          </p>
        )}

        {mudou && (
          <button type="button" className="botao secundario" disabled={enviando}
            onClick={() => void carregar()}>
            Descartar
          </button>
        )}
        <button type="button" className="botao"
          disabled={enviando || (mudou && !aberta)}
          onClick={() => (diff.saindo.length > 0
            ? setConfirmando(true) : void enviar())}>
          {enviando ? 'Salvando…'
            : mudou ? 'Salvar a programação'
              : 'Confirmar a programação'}
        </button>

        {mudou && !aberta && (
          <p className="sub aviso-linha">
            A OS está cancelada: a programação dela não muda mais.
          </p>
        )}
      </section>
      )}

      {confirmando && (
        <Dialogo aberto titulo="Excluir da programação?"
          aoFechar={() => setConfirmando(false)}>
          <p>
            {diff.saindo.length} ensaio(s) sairão da programação desta OS e
            serão <strong>cancelados</strong>. Ensaio cancelado não volta —
            se precisar dele de novo, é programar outro.
          </p>
          <ul className="lista-saindo">
            {diff.saindo.slice(0, 12).map((k) => {
              const [aId, mId] = k.split('|');
              const am = dados.amostras.find((x) => x.id === aId);
              const me = dados.metodos.find((x) => x.id === mId);
              return (
                <li key={k}>
                  <strong>{me?.codigo ?? '—'}</strong> em {am ? nomeDaAmostra(nomeNaGrade(am)) : '—'}
                </li>
              );
            })}
            {diff.saindo.length > 12 && (
              <li className="sub">e mais {diff.saindo.length - 12}…</li>
            )}
          </ul>
          <div className="acoes">
            <button type="button" onClick={() => setConfirmando(false)}>
              Voltar à grade
            </button>
            <button type="button" className="principal" disabled={enviando}
              onClick={() => void enviar()}>
              {enviando ? 'Salvando…' : 'Excluir e salvar'}
            </button>
          </div>
        </Dialogo>
      )}
    </div>
  );
}

/* ── A gaveta de instrução ───────────────────────────────────────────── */

interface AlvoDaGaveta {
  metodo: MetodoDaGrade;
  ensaios: { id: string; amostra: string }[];
  /** 0182: células de amostra que ainda não chegou. */
  previstos?: { previstaId: string; metodoId: string; amostra: string }[];
  status: string;
  texto: string;
}

/** O nome pelo qual uma amostra «por chegar» é reconhecida na conversa. */
function rotuloDaPrevista(a: { codigo: string; identificacao_recebida: string | null;
  ponto_investigacao: string | null }) {
  return a.identificacao_recebida || a.ponto_investigacao || a.codigo;
}

/**
 * RN-248 (onda 303): a amostra da grade pelo nome do cliente, com o código
 * AM logo depois. A que ainda não chegou não tem código — no lugar dele vai
 * «por chegar». A grade é de uma OS só: a OS não se repete.
 */
function nomeNaGrade(a: { codigo: string; identificacao_recebida: string | null;
  prevista?: boolean }): AmostraComNome {
  return { codigo: a.prevista ? 'por chegar' : a.codigo, identificacao: a.identificacao_recebida };
}

/**
 * A instrução técnica de um ensaio.
 *
 * ── Por que ela é do ENSAIO, e não do método ───────────────────────────
 *
 * «a instrução deve ser por amostra e por ensaio, pois para amostras
 *  diferentes pode haver variação.» Como o ensaio já é o par (amostra,
 *  método), gravar nele é gravar exatamente onde a variação existe.
 *  Gravar no método faria a segunda amostra herdar em silêncio a receita
 *  da primeira.
 *
 * ── O motivo ───────────────────────────────────────────────────────────
 *
 * Enquanto o ensaio está `programado`, a instrução ainda é um plano:
 * corrigir é de graça. Depois disso alguém já leu aquele texto e agiu, e
 * trocar em silêncio produz o pior dos registros — o técnico executou uma
 * coisa e a base conta outra. Quem cobra o motivo é o gatilho da `0083`;
 * a tela só pergunta antes para a recusa não chegar depois do trabalho.
 */
function GavetaDeInstrucao({
  alvo, aoFechar, aoGravar,
}: { alvo: AlvoDaGaveta; aoFechar(): void; aoGravar(): Promise<void> }) {
  const [texto, setTexto] = useState(alvo.texto);
  const [motivo, setMotivo] = useState('');
  const [nomeModelo, setNomeModelo] = useState('');
  const [salvandoModelo, setSalvandoModelo] = useState(false);
  const [modelos, setModelos] = useState<ModeloDeInstrucao[]>([]);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<unknown>(null);

  const exigeMotivo = alvo.status !== 'programado';

  useEffect(() => {
    void modelosDeInstrucao(alvo.metodo.id).then(setModelos).catch(() => {});
  }, [alvo.metodo.id]);

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      await gravarInstrucao({
        ensaioIds: alvo.ensaios.map((e) => e.id),
        previstos: (alvo.previstos ?? [])
          .map((p) => ({ previstaId: p.previstaId, metodoId: p.metodoId })),
        instrucao: texto,
        motivo: motivo.trim() || undefined,
      });
      await aoGravar();
    } catch (e) { setErro(e); } finally { setOcupado(false); }
  };

  const guardarModelo = async () => {
    setSalvandoModelo(true); setErro(null);
    try {
      const m = await salvarModeloDeInstrucao({
        metodoId: alvo.metodo.id, nome: nomeModelo.trim(), texto: texto.trim(),
      });
      setModelos((l) => [m, ...l.filter((x) => x.id !== m.id)]);
      setNomeModelo('');
    } catch (e) { setErro(e); } finally { setSalvandoModelo(false); }
  };

  return (
    <Dialogo aberto aoFechar={aoFechar}
      titulo={`Instrução — ${alvo.metodo.codigo}`}>
      <p className="sub">
        {(() => {
          const todos = [...alvo.ensaios.map((e) => e.amostra),
            ...(alvo.previstos ?? []).map((p) => p.amostra)];
          const porChegar = alvo.previstos?.length ?? 0;
          if (todos.length === 1) {
            return `Vale para o ensaio de ${alvo.metodo.codigo} em ${todos[0]}`
              + (porChegar ? ' — amostra que ainda vai chegar; o ensaio nasce com ela e já traz esta instrução.' : '.');
          }
          return `Vale para ${todos.length} ensaios de ${alvo.metodo.codigo}`
            + ` (${todos.join(', ')})`
            + (porChegar ? ` — ${porChegar} em amostra que ainda vai chegar.` : '.');
        })()}
        {' '}O técnico lê antes de lançar, e ela sai impressa no PDF da OS —
        não no laudo.
      </p>

      <textarea className="campo-instrucao" value={texto} spellCheck={false}
        onChange={(e) => setTexto(e.target.value)}
        placeholder="Degraus de carga, velocidade, tensões confinantes, tempo por estágio, condição de inundação…" />

      {modelos.length > 0 && (
        <div className="modelos-instrucao">
          <span className="rot">Modelos</span>
          {modelos.map((m) => (
            <button key={m.id} type="button" title={m.texto}
              onClick={() => { setTexto(m.texto); void contarUsoDoModelo(m.id); }}>
              {m.nome}
            </button>
          ))}
        </div>
      )}

      {/*
        * «Salvar como modelo» é cadastro, e não atalho de tela.
        *
        * Mesma decisão das baterias: a receita da casa muda, e mudar não
        * pode exigir versão nova do sistema.
        */}
      {texto.trim().length >= 5 && (
        <div className="salvar-modelo">
          <Campo rotulo="Guardar esta receita como modelo do método"
            value={nomeModelo} placeholder="Ex.: NBR 16853 — 7 cargas / 3 descargas"
            onChange={(e) => setNomeModelo(e.target.value)} />
          <button type="button" disabled={nomeModelo.trim().length < 3 || salvandoModelo}
            onClick={() => void guardarModelo()}>
            {salvandoModelo ? 'Guardando…' : 'Guardar modelo'}
          </button>
        </div>
      )}

      {exigeMotivo && (
        <Campo rotulo="Este ensaio já saiu de «programado» — diga por que a instrução muda"
          value={motivo} onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ex.: cliente pediu estágio adicional de 1600 kPa" />
      )}

      {erro ? <ErroDaApi erro={erro} /> : null}

      <div className="acoes">
        <button type="button" onClick={aoFechar}>Fechar</button>
        <button type="button" className="principal" disabled={ocupado}
          onClick={() => void gravar()}>
          {ocupado ? 'Gravando…' : 'Gravar a instrução'}
        </button>
      </div>
    </Dialogo>
  );
}

/* ── Instrução para várias amostras ──────────────────────────────────── */

/**
 * Um ensaio, várias amostras já programadas, uma instrução.
 *
 * «Permita que seja adicionada instruções após programar a amostra e
 *  também que seja feito isso para mais de uma amostra da mesma OS ao
 *  mesmo tempo» (Humberto, 14/09/2026). «Programar em massa» já escrevia a
 * receita uma vez — mas só NA HORA de programar; depois de salvo, era uma
 * gaveta por linha. Aqui se escolhe o método, marcam-se as amostras que
 * já o têm (recebidas ou por chegar) e a gaveta de sempre grava em todas.
 *
 * Só entra amostra cujo par (amostra, método) já está gravado: ensaio
 * vivo ou previsão salva. O que está só marcado na grade e não foi salvo
 * ainda não existe para o servidor.
 */
function InstruirVarias({
  dados, disponiveis, existente, previstoSalvo, aoFechar, aoEscolher,
}: {
  dados: GradeDaOs;
  disponiveis: AmostraDaGrade[];
  existente: Map<string, { id: string; status: string; instrucao: string | null }>;
  previstoSalvo: Map<string, { previstaId: string; instrucao: string | null }>;
  aoFechar(): void;
  aoEscolher(alvo: AlvoDaGaveta): void;
}) {
  const metodosComAlgo = useMemo(() => dados.metodos.filter((m) =>
    disponiveis.some((a) => existente.has(chave(a.id, m.id))
      || previstoSalvo.has(chave(a.id, m.id)))), [dados, disponiveis, existente, previstoSalvo]);
  const [metodoId, setMetodoId] = useState(metodosComAlgo[0]?.id ?? '');
  const metodo = dados.metodos.find((m) => m.id === metodoId);

  const candidatas = useMemo(() => disponiveis
    .map((a) => {
      const k = chave(a.id, metodoId);
      const ja = existente.get(k);
      const pv = ja ? undefined : previstoSalvo.get(k);
      if (!ja && !pv) return null;
      if (ja && ['aprovado', 'cancelado', 'substituido'].includes(ja.status)) return null;
      return { a, ja, pv, instrucao: ja?.instrucao ?? pv?.instrucao ?? null };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null), [disponiveis, metodoId, existente, previstoSalvo]);

  const [escolhidas, setEscolhidas] = useState<Set<string>>(new Set());
  useEffect(() => { setEscolhidas(new Set(candidatas.map((c) => c.a.id))); }, [candidatas]);

  const seguir = () => {
    if (!metodo) return;
    const sel = candidatas.filter((c) => escolhidas.has(c.a.id));
    const ensaios = sel.filter((c) => c.ja)
      .map((c) => ({ id: c.ja!.id, amostra: nomeDaAmostra(nomeNaGrade(c.a)) }));
    const previstos = sel.filter((c) => c.pv)
      .map((c) => ({ previstaId: c.pv!.previstaId, metodoId, amostra: rotuloDaPrevista(c.a) }));
    /* O motivo é cobrado se ALGUM dos ensaios já saiu de «programado». */
    const foraDePrograma = sel.find((c) => c.ja && c.ja.status !== 'programado');
    const textos = new Set(sel.map((c) => c.instrucao ?? ''));
    aoEscolher({
      metodo, ensaios, previstos,
      status: foraDePrograma ? foraDePrograma.ja!.status : 'programado',
      texto: textos.size === 1 ? [...textos][0] : '',
    });
  };

  return (
    <Dialogo aberto titulo="Instrução para várias amostras" aoFechar={aoFechar}>
      <p className="sub">
        Escolha o ensaio e as amostras; a instrução se escreve uma vez e
        vale para todas. Amostra que ainda vai chegar entra também — o
        ensaio dela nasce com a instrução quando ela chegar.
      </p>
      {metodosComAlgo.length === 0 ? (
        <Aviso tipo="info" titulo="Nada programado ainda.">
          Marque os ensaios na grade e salve a programação; depois a
          instrução pode ser escrita para várias amostras de uma vez.
        </Aviso>
      ) : (
        <>
          <label className="campo">
            <span className="rotulo">Ensaio</span>
            <select value={metodoId} onChange={(e) => setMetodoId(e.currentTarget.value)}>
              {metodosComAlgo.map((m) => (
                <option key={m.id} value={m.id}>{m.codigo} — {m.nome}</option>
              ))}
            </select>
          </label>
          <div className="campo" role="group" aria-label="Amostras">
            <span className="rotulo">
              Amostras com {metodo?.codigo} programado ({escolhidas.size} de {candidatas.length})
            </span>
            <div className="linha" style={{ gap: '.5rem', marginBottom: '.4rem' }}>
              <button type="button" className="botao secundario estreito"
                onClick={() => setEscolhidas(new Set(candidatas.map((c) => c.a.id)))}>
                Todas
              </button>
              <button type="button" className="botao secundario estreito"
                onClick={() => setEscolhidas(new Set())}>
                Nenhuma
              </button>
            </div>
            <div className="lista">
              {candidatas.map((c) => (
                <label key={c.a.id} className="item" style={{ display: 'flex', gap: '.6rem', alignItems: 'center' }}>
                  <input type="checkbox" checked={escolhidas.has(c.a.id)}
                    onChange={(e) => setEscolhidas((s) => {
                      const n = new Set(s);
                      if (e.target.checked) n.add(c.a.id); else n.delete(c.a.id);
                      return n;
                    })} />
                  {c.pv ? <span className="cod mono">{rotuloDaPrevista(c.a)}</span>
                    : <NomeDaAmostra amostra={nomeNaGrade(c.a)} emLinha />}
                  <span className="sub">
                    {c.pv ? 'por chegar' : rotulo(c.ja!.status)}
                    {c.instrucao ? ' · já tem instrução' : ''}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="acoes">
            <button type="button" onClick={aoFechar}>Fechar</button>
            <button type="button" className="principal" disabled={escolhidas.size === 0}
              onClick={seguir}>
              Escrever a instrução para {escolhidas.size} amostra(s)
            </button>
          </div>
        </>
      )}
    </Dialogo>
  );
}

/* ── Programar em massa ──────────────────────────────────────────────── */

/**
 * Um ensaio, muitas amostras, uma instrução.
 *
 * «Caso tenha programações em massa para várias amostras da mesma OS,
 *  poderia haver um botão de programação, assim é possível por exemplo,
 *  programar ensaios de adensamento para X amostras. Mas também seria
 *  possível programar individualmente.» — Humberto, 31/08/2026.
 *
 * A grade já fazia isso pela coluna; o que faltava era o caso em que a
 * pessoa está na SANFONA, não quer trocar de modo, e precisa escolher um
 * subconjunto das amostras — «só as que ainda não têm», por exemplo. E,
 * sobretudo, escrever a receita UMA VEZ para todas.
 */
function ProgramarEmMassa({
  dados, disponiveis, existente, aoFechar, aoAplicar,
}: {
  dados: GradeDaOs;
  disponiveis: GradeDaOs['amostras'];
  existente: Map<string, { id: string; status: string; instrucao: string | null }>;
  aoFechar(): void;
  aoAplicar(celulas: string[], metodoIds: string[], instrucao: string): void;
}) {
  const [escolhidos, setEscolhidos] = useState<Set<string>>(new Set());
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [instrucao, setInstrucao] = useState('');
  const [busca, setBusca] = useState('');
  const [modelos, setModelos] = useState<ModeloDeInstrucao[]>([]);

  /* Modelos só quando há UM método escolhido: duas receitas de métodos
     diferentes numa caixa de texto só seria uma instrução errada para um
     dos dois. */
  useEffect(() => {
    const ids = [...escolhidos];
    if (ids.length !== 1) { setModelos([]); return; }
    void modelosDeInstrucao(ids[0]).then(setModelos).catch(() => {});
  }, [escolhidos]);

  const achados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return dados.metodos.slice(0, 24);
    return dados.metodos.filter((m) => `${m.codigo} ${m.nome} ${m.escopo_nome}`
      .toLowerCase().includes(t)).slice(0, 40);
  }, [dados.metodos, busca]);

  const novas = useMemo(() => {
    const c: string[] = [];
    for (const a of sel) {
      for (const m of escolhidos) {
        if (existente.has(chave(a, m))) continue;
        c.push(chave(a, m));
      }
    }
    return c;
  }, [sel, escolhidos, existente]);

  const repetidos = sel.size * escolhidos.size - novas.length;

  return (
    <Dialogo aberto titulo="Programar em massa" aoFechar={aoFechar}>
      <p className="sub">
        Escolha o ensaio, escolha as amostras, escreva a instrução uma vez.
        Nada é gravado agora: a marcação entra na grade e vai junto no
        «Salvar a programação».
      </p>

      <Campo rotulo="Ensaio" value={busca} autoFocus
        placeholder="ADEN, cisalhamento, granulometria…"
        onChange={(e) => setBusca(e.target.value)} />
      <div className="metodos-massa">
        {achados.map((m) => (
          <button key={m.id} type="button"
            className={`escopo-tag escopo-${m.escopo}`}
            aria-pressed={escolhidos.has(m.id)} title={m.nome}
            onClick={() => setEscolhidos((s) => {
              const n = new Set(s);
              if (n.has(m.id)) n.delete(m.id); else n.add(m.id);
              return n;
            })}>
            <span className="pt" aria-hidden="true" />{m.codigo}
          </button>
        ))}
      </div>

      <div className="atalhos-massa">
        <span className="rot">Amostras</span>
        <button type="button"
          onClick={() => setSel(new Set(disponiveis.map((a) => a.id)))}>
          todas ({disponiveis.length})
        </button>
        {(['deformada', 'indeformada', 'cp_moldado'] as const).map((t) => {
          const n = disponiveis.filter((a) => a.tipo_amostra === t).length;
          if (n === 0) return null;
          return (
            <button key={t} type="button"
              onClick={() => setSel(new Set(disponiveis
                .filter((a) => a.tipo_amostra === t).map((a) => a.id)))}>
              {t === 'deformada' ? 'deformadas'
                : t === 'indeformada' ? 'indeformadas' : 'CPs moldados'} ({n})
            </button>
          );
        })}
        <button type="button"
          onClick={() => setSel(new Set(disponiveis
            .filter((a) => [...escolhidos].some((m) => !existente.has(chave(a.id, m))))
            .map((a) => a.id)))}>
          só as que ainda não têm
        </button>
        <button type="button" onClick={() => setSel(new Set())}>limpar</button>
      </div>

      <div className="lista-amostras-massa">
        {disponiveis.map((a) => {
          const jaTem = [...escolhidos]
            .filter((m) => existente.has(chave(a.id, m)))
            .map((m) => dados.metodos.find((x) => x.id === m)?.codigo)
            .filter(Boolean);
          return (
            <label key={a.id}>
              <input type="checkbox" checked={sel.has(a.id)}
                onChange={(e) => setSel((s) => {
                  const n = new Set(s);
                  if (e.target.checked) n.add(a.id); else n.delete(a.id);
                  return n;
                })} />
              <NomeDaAmostra amostra={nomeNaGrade(a)} emLinha />
              <span className="det">
                {[a.ponto_investigacao,
                  faixaProfundidade(a.profundidade_inicial, a.profundidade_final)
                  && `${faixaProfundidade(a.profundidade_inicial,
                    a.profundidade_final)} m`].filter(Boolean).join(' · ') || '—'}
              </span>
              {jaTem.length > 0 && (
                <span className="ja mono">já tem {jaTem.join(', ')}</span>
              )}
            </label>
          );
        })}
      </div>

      <label className="rot-instrucao" htmlFor="instrucao-massa">
        Instrução (opcional) — vale para todas as escolhidas
      </label>
      <textarea id="instrucao-massa" className="campo-instrucao"
        value={instrucao} spellCheck={false}
        onChange={(e) => setInstrucao(e.target.value)}
        placeholder="Ex.: degraus 12,5 · 25 · 50 · 100 · 200 · 400 · 800 kPa, 24 h por estágio." />

      {modelos.length > 0 && (
        <div className="modelos-instrucao">
          <span className="rot">Modelos</span>
          {modelos.map((m) => (
            <button key={m.id} type="button" title={m.texto}
              onClick={() => { setInstrucao(m.texto); void contarUsoDoModelo(m.id); }}>
              {m.nome}
            </button>
          ))}
        </div>
      )}

      <p className="resumo-diff">
        {escolhidos.size === 0 || sel.size === 0 ? (
          <span className="sub">nada escolhido ainda</span>
        ) : (
          <>
            <span className="entra">+{novas.length} ensaio(s)</span>
            <span className="sub">em {sel.size} amostra(s)</span>
            {repetidos > 0 && (
              <span className="sub">
                {repetidos} já existia(m) e não se repete(m)
              </span>
            )}
          </>
        )}
      </p>

      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="principal"
          disabled={novas.length === 0}
          onClick={() => aoAplicar(novas, [...escolhidos], instrucao)}>
          Marcar na grade
        </button>
      </div>
    </Dialogo>
  );
}

/* ── A célula ────────────────────────────────────────────────────────── */

/**
 * A célula da grade.
 *
 * Marca = «esta OS faz este ensaio»; desmarcar é tirar. O que não é caixa
 * é o ensaio que já virou trabalho de bancada — desmarcar aquilo jogaria
 * fora trabalho feito, e o caminho para desfazer é a revisão do ensaio,
 * que preserva o anterior. A cor não decide sozinha: o `title` diz o
 * estado por extenso.
 *
 * O anel em volta da marca diz que a célula tem INSTRUÇÃO. Clicar com a
 * tecla Alt abre a gaveta em vez de alternar — na grade densa não cabe um
 * segundo botão por célula, e obrigar a trocar para a sanfona só para ler
 * uma receita seria atrito.
 */

function Celula({
  marcada, previsto, ja, fora, travada: tr, temInstrucao,
  rotulo: rot, aoAlternar, aoInstruir,
}: {
  marcada: boolean; previsto: boolean; fora: boolean; travada: boolean;
  temInstrucao: boolean;
  ja?: { id: string; status: string };
  rotulo: string; aoAlternar: () => void; aoInstruir?: () => void;
}) {
  if (tr && ja) {
    return (
      <Link to={`/ensaio/${ja.id}`} className={`ja-programado ${ja.status}`}
        title={`${rot} — ${rotulo(ja.status)}, já em bancada: não sai pela grade`}>
        {simboloDoEnsaio(ja.status)}
      </Link>
    );
  }

  const classe = ['caixa-grade',
    previsto ? 'previsto' : '',
    ja ? 'gravado' : '',
    temInstrucao ? 'com-instrucao' : ''].filter(Boolean).join(' ');

  return (
    <label className={classe}
      title={[
        ja ? `${rot} — programado; desmarcar exclui da programação`
          : `${rot} — marcar para programar`,
        aoInstruir ? 'Alt+clique abre a instrução' : '',
        temInstrucao ? 'tem instrução' : '',
      ].filter(Boolean).join(' · ')}>
      <input type="checkbox" checked={marcada} disabled={fora}
        aria-label={[rot, previsto ? '(previsto no escopo)' : '',
          ja ? '(já programado)' : '',
          temInstrucao ? '(com instrução)' : ''].filter(Boolean).join(' ')}
        onChange={aoAlternar}
        onClick={(e) => {
          if (e.altKey && aoInstruir) { e.preventDefault(); aoInstruir(); }
        }} />
    </label>
  );
}

/* ── Procurar um ensaio no catálogo ──────────────────────────────────── */

/**
 * Existe porque a grade mostra poucas colunas de propósito. Sem busca, o
 * «acrescentar» seria uma lista de 55 — que é a parede que a onda 35 veio
 * derrubar. Com busca, o catálogo inteiro cabe atrás de três teclas.
 */
function BuscarMetodo({
  metodos, jaVisiveis, aoEscolher,
}: {
  metodos: MetodoDaGrade[];
  jaVisiveis: Set<string>;
  aoEscolher: (id: string) => void;
}) {
  const [busca, setBusca] = useState('');

  const achados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    const livres = metodos.filter((m) => !jaVisiveis.has(m.id));
    if (!t) return livres;
    return livres.filter((m) => `${m.codigo} ${m.nome} ${m.grupo ?? ''}`
      .toLowerCase().includes(t));
  }, [metodos, jaVisiveis, busca]);

  /* Agrupado pelo ESCOPO desde a onda 46 — quem procura um ensaio pensa
     na bancada que vai ocupar, e não na subclasse da lista de preços. */
  const porEscopo = useMemo(() => {
    const g = new Map<string, MetodoDaGrade[]>();
    for (const m of achados) {
      const k = m.escopo_nome ?? 'Outros';
      if (!g.has(k)) g.set(k, []);
      g.get(k)!.push(m);
    }
    return [...g.entries()];
  }, [achados]);

  return (
    <section className="bloco busca-metodo">
      <Campo rotulo="Procurar ensaio do escopo da OS" value={busca} autoFocus
        placeholder="ADEN, granulometria, cisalhamento…"
        onChange={(e) => setBusca(e.target.value)} />

      {achados.length === 0
        ? <Vazio>
          {busca.trim()
            ? <>Nenhum ensaio do escopo casa com «{busca}».</>
            : <>Todos os ensaios do escopo já estão na grade.</>}
          {' '}O que não está no escopo entra pela OS («Incluir na OS»).
        </Vazio>
        : porEscopo.map(([escopo, lista]) => (
          <div key={escopo} className="grupo-metodo">
            <h3>{escopo}</h3>
            <ul className="grade-selecao">
              {lista.map((m) => (
                <li key={m.id}>
                  <button type="button" onClick={() => aoEscolher(m.id)}>
                    <span className="codigo">{m.codigo}</span>
                    <span className="nome">{m.nome}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
    </section>
  );
}

/**
 * Reprogramar a OS — data nova, e o porquê.
 *
 * O §8 pede «reprogramar sem apagar a programação original» e «registrar
 * o motivo dos atrasos». A data anterior continua legível na trilha de
 * auditoria, e é de lá que sai a resposta para «por que esta OS
 * atrasou?» seis meses depois.
 *
 * Vale para todos os ensaios da OS que ainda vão acontecer. É assim que
 * o atraso acontece — a sonda quebrou e a semana inteira anda três dias —,
 * e reprogramar de um em um deixaria metade da semana numa data e metade
 * na outra.
 */
function DialogoReprogramar({ ensaioIds, aoFechar, aoPronto }: {
  ensaioIds: string[];
  aoFechar(): void;
  aoPronto(n: number): void;
}) {
  const [dataPrevista, setDataPrevista] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      const r = await reprogramar({ ensaioIds, dataPrevista, motivo });
      aoPronto(r.reprogramados);
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Reprogramar a OS" aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <p className="sub">
        {ensaioIds.length} ensaio(s) que ainda vão acontecer. Aprovado,
        cancelado e substituído ficam de fora — eles já não vão acontecer
        de novo.
      </p>
      <Campo rotulo="Nova data prevista" type="date" value={dataPrevista}
        onChange={(ev) => setDataPrevista(ev.target.value)} />
      <label className="campo">
        <span className="rotulo">Por quê?</span>
        <textarea rows={3} value={motivo}
          onChange={(ev) => setMotivo(ev.target.value)}
          placeholder="a sonda quebrou e a equipe só voltou na quinta…" />
      </label>
      <p className="sub">
        A data anterior não se perde: ela fica na trilha de auditoria com
        este motivo, e é isso que responde, depois, por que a OS atrasou.
      </p>
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="principal"
          disabled={ocupado || !dataPrevista || motivo.trim().length < 10}
          onClick={() => { void gravar(); }}>
          {ocupado ? 'Gravando…' : 'Reprogramar'}
        </button>
      </div>
    </Dialogo>
  );
}
